import React, { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Heading from '@atoms/Heading';
import HeadingSize from '@atoms/Heading/sizes';
import GrayContainer from '@atoms/GrayContainer';
import Info from '@atoms/Info';
import InfoType from '@atoms/Info/type';
import LoadingBox from '@atoms/LoadingBox';
import { ButtonProps, ButtonType } from '@atoms/Button';

import NextPrevButtons from '@molecules/NextPrevButtons';
import Input from '@molecules/Input';
import LayerPatterns from '@organisms/LayerPattern';
import Veil from '@molecules/Veil';
import ToggleButtons from '@molecules/ToggleButtons';
import Measures from '@molecules/Measures';

import OverlapRange from '@organisms/OverlapRange';

import useBreakpoint from 'hooks/useBreakpoint';
import useLayoutConfigState from 'hooks/useLayoutConfigState';
import useCalculatorState from 'hooks/useCalculatorState';
import useImagesState from 'hooks/useImagesState';

import {
  LayerConfiguration,
  LayerPatternType,
  UnitPackaging,
} from 'enums/calculator';

import { ReactComponent as BoxIcon } from '@icons/box.svg';

import styles from './styles';

// import { InlineSvg } from 'images/InlineSvg';

// const BoxIcon = ({ className }: { className?: string }) => (
//   <InlineSvg svgContent={_BoxIcon} className={className} />
// );
const LazyComponent = React.lazy(() => import('@organisms/Pallet3D/Pallet3D'));

const Step3 = () => {
  const { t } = useTranslation();
  const [errorUnits, setErrorUnits] = useState<number>();

  const { isMobile } = useBreakpoint();
  const {
    activeItem,
    canGoNext,
    layerConfigType,
    setLayerConfigType,
    setUnits,
    units,
    type,
  } = useLayoutConfigState();

  const isOverhang = type === LayerPatternType.OVERHANG;

  const { nextStep, packagingType, setPackagingType, view3D, toggle3DView } =
    useCalculatorState();

  const { isSaving, save2DImage, on3DPalletReady } = useImagesState();

  const displayOverlapToggle =
    packagingType === UnitPackaging.BAG ||
    packagingType === UnitPackaging.OVERLAP_BAG;

  const onUnitsChange = (value: string) => {
    setUnits(Number(value) || null);
  };

  const stringUnits = Number(units) > 1 ? t('units') : t('unit');
  const infoMessage = (
    <Trans
      components={{ strong: <strong /> }}
      values={{ units: units || 0, stringUnits }}
    >
      errorNoPatterns
    </Trans>
  );
  const showError = errorUnits === 0;
  const noResults = showError && (
    <Info message={infoMessage} type={InfoType.ALERT} className="!my-8" />
  );

  const onListChange = (patternsQuantity: number | null) => {
    if (errorUnits !== patternsQuantity) {
      setErrorUnits(Number(patternsQuantity));
    }
  };

  const toggle: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: LayerConfiguration.AUTO,
      active: layerConfigType === LayerConfiguration.AUTO,
      className: styles.toggleButton1,
    },
    {
      type: ButtonType.PILL,
      children: LayerConfiguration.MANUAL,
      active: layerConfigType === LayerConfiguration.MANUAL,
      className: styles.toggleButton2,
    },
  ];

  const layerConfigOnChange = (item: ButtonProps) =>
    setLayerConfigType(item.children as LayerConfiguration);

  const isAuto = layerConfigType === LayerConfiguration.AUTO;
  const manualInput = (
    <Input
      id="units"
      type="number"
      onChange={onUnitsChange}
      value={String(units) || ''}
      error={Boolean(units && units < 0)}
    />
  );
  const viewToggleButtonText = view3D ? t('View in 2D') : t('View in 3D');
  const viewToggleButton = (
    <button
      type="button"
      className={styles.viewToggleButton}
      onClick={toggle3DView}
    >
      <BoxIcon className="mr-4" />
      {viewToggleButtonText}
    </button>
  );

  const savingImagesComponent = isSaving && (
    <Veil message={t('Saving image...')} />
  );

  /**
   * Save 2D Image before continue to the next step
   */
  const onNextButtonClick = () => {
    if (view3D) {
      toggle3DView();
    }
    save2DImage().then(() => {
      nextStep();
    });
  };

  const navigationButtons = (
    <NextPrevButtons
      nextButtonEnabled={canGoNext()}
      onNextButtonClick={onNextButtonClick}
    />
  );

  return (
    <section className="lg:flex justify-between md:gap-12">
      <section className="user-input lg:w-5/12">
        <section id="unit-information">
          <Heading size={HeadingSize.H1}>{t('Layer configuration')}</Heading>
          <p>{t('home:step3.description')}</p>
          {noResults}
          <div className={styles.toggleSection}>
            <Heading className="flex items-center !mt-10">
              # Units per layer
            </Heading>
            <ToggleButtons
              className={styles.toggle}
              items={toggle}
              onChange={layerConfigOnChange}
            />
          </div>
          {!isAuto && manualInput}
          <LayerPatterns unitsPerLayer={units} onListChange={onListChange} />
          {displayOverlapToggle && (
            <Input
              type="checkbox"
              id="toggleOverlap"
              label="Overlap"
              checked={Boolean(packagingType === UnitPackaging.OVERLAP_BAG)}
              onChange={() => {
                if (packagingType === UnitPackaging.BAG) {
                  setPackagingType(UnitPackaging.OVERLAP_BAG);
                } else if (packagingType === UnitPackaging.OVERLAP_BAG) {
                  setPackagingType(UnitPackaging.BAG);
                }
              }}
            />
          )}
          {packagingType === UnitPackaging.OVERLAP_BAG && (
            <React.Fragment>
              <OverlapRange direction="horizontal" label={t('Horizontal')} />
              <OverlapRange direction="vertical" label={t('Vertical')} />
            </React.Fragment>
          )}
        </section>
        {!isMobile && navigationButtons}
      </section>
      <section className="block lg:w-7/12">
        {isMobile && (
          <div className={styles.toggleButtonMobileContainer}>
            {viewToggleButton}
          </div>
        )}
        <GrayContainer className={styles.grayContainerClasses}>
          {!isMobile && viewToggleButton}
          {savingImagesComponent}
          <Suspense
            fallback={
              <LoadingBox border={false} bigText className="text-mainBlue">
                {t('Loading 3D model data...')}
              </LoadingBox>
            }
          >
            {Number(errorUnits) > 0 && (
              <LazyComponent
                activePattern={activeItem}
                view3D={view3D}
                onReady={on3DPalletReady}
                overhang={isOverhang}
              />
            )}
          </Suspense>
          {showError && (
            <LoadingBox border={false} bigText className="text-mainBlue">
              {t('No pattern to display')}
            </LoadingBox>
          )}
        </GrayContainer>
        {isOverhang && (
          <div className="p-2 bg-lightGray -translate-y-4 lg:-translate-y-8 rounded-bl-2xl rounded-br-2xl">
            <Measures />
          </div>
        )}
        <small className="block text-darkGray text-xs !mt-5 leading-[18px]">
          <p className="!mb-0">
            The rendering is theoretical and may not accurately reflect reality.
          </p>
          <p className="!mb-0">
            We make no warranties or representations as to its accuracy
          </p>
        </small>
      </section>
      {isMobile && navigationButtons}
    </section>
  );
};

export default Step3;
